import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import fireplaceLogo from '../assets/fireplace-logo.png';

interface LandingPageProps {
  isVisible: boolean;
  onReveal: () => void;
}

// Generate random dots for the background
interface BackgroundDot {
  id: number;
  x: number; // percentage
  y: number; // percentage
  size: number; // px
  color: string;
  opacity: number;
  duration: number; // animation duration
  delay: number; // animation delay
  yOffset: number; // how much to float up/down
}

const DOT_COLORS = [
  '#FF6B35', // Primary orange
  '#FF8C42', // Medium orange
  '#FFA366', // Light orange
  '#FFD23F', // Primary yellow
  '#FFE082', // Light yellow
  '#FFECB3', // Pale yellow
];

function generateDots(count: number): BackgroundDot[] {
  const dots: BackgroundDot[] = [];
  for (let i = 0; i < count; i++) {
    // Create clusters by biasing positions
    const cluster = Math.random();
    let x: number, y: number;
    
    if (cluster < 0.25) {
      // Top-left cluster
      x = 10 + Math.random() * 25;
      y = 15 + Math.random() * 25;
    } else if (cluster < 0.5) {
      // Center cluster
      x = 35 + Math.random() * 30;
      y = 35 + Math.random() * 30;
    } else if (cluster < 0.75) {
      // Bottom-right cluster
      x = 60 + Math.random() * 30;
      y = 55 + Math.random() * 35;
    } else {
      // Scattered
      x = 5 + Math.random() * 90;
      y = 5 + Math.random() * 90;
    }
    
    // Depth: smaller = further away
    const depth = Math.random();
    const size = depth < 0.3 ? 4 + Math.random() * 4 : // Small (far)
                 depth < 0.7 ? 8 + Math.random() * 6 : // Medium
                 14 + Math.random() * 10; // Large (close)
    
    const opacity = depth < 0.3 ? 0.15 + Math.random() * 0.15 :
                    depth < 0.7 ? 0.25 + Math.random() * 0.2 :
                    0.4 + Math.random() * 0.25;
    
    dots.push({
      id: i,
      x,
      y,
      size,
      color: DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)],
      opacity,
      duration: 3 + Math.random() * 4, // 3-7 seconds
      delay: Math.random() * 3, // 0-3 second delay
      yOffset: 3 + Math.random() * 8, // 3-11px float
    });
  }
  return dots;
}

// NOTE: Detailed explanations moved to "Wat Dis?" modal in App.tsx

export function LandingPage({ isVisible, onReveal }: LandingPageProps) {
  const [isRevealing, setIsRevealing] = useState(false);
  
  // Generate dots once and memoize
  const backgroundDots = useMemo(() => generateDots(50), []);

  useEffect(() => {
    if (isVisible) {
      setIsRevealing(false);
    }
  }, [isVisible]);

  const handleExplore = () => {
    setIsRevealing(true);
    setTimeout(() => {
      onReveal();
    }, 500);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[100]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Background */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 1 }}
            animate={{ opacity: isRevealing ? 0 : 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{
              background: 'linear-gradient(135deg, #FFFBF0 0%, #FFF8E7 50%, #FFFBF0 100%)',
            }}
          />

          {/* Full-screen animated dots background */}
          <motion.div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: isRevealing ? 0 : 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Slowly rotating container for immersive effect */}
            <motion.div
              className="absolute inset-0"
              animate={{ 
                rotate: [0, 3, -3, 0],
              }}
              transition={{ 
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ transformOrigin: '50% 50%' }}
            >
              {backgroundDots.map((dot) => (
                <motion.div
                  key={dot.id}
                  className="absolute rounded-full"
                  style={{
                    left: `${dot.x}%`,
                    top: `${dot.y}%`,
                    width: dot.size,
                    height: dot.size,
                    backgroundColor: dot.color,
                    opacity: dot.opacity,
                  }}
                  animate={{
                    y: [0, -dot.yOffset, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: dot.duration,
                    repeat: Infinity,
                    delay: dot.delay,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </motion.div>
          </motion.div>

          {/* Subtle decorative accents */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0.4 }}
            animate={{ opacity: isRevealing ? 0 : 0.4 }}
            transition={{ duration: 0.4 }}
          >
            <div 
              className="absolute -top-32 -right-32 w-96 h-96 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255, 107, 53, 0.08) 0%, transparent 70%)',
              }}
            />
            <div 
              className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255, 210, 63, 0.06) 0%, transparent 70%)',
              }}
            />
          </motion.div>

          {/* Floating annotation for background dots */}
          <motion.div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-none z-20"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: isRevealing ? 0 : 0.8, y: isRevealing ? 10 : 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <span className="text-xs text-text-secondary/70 bg-white/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-warm-border/30 shadow-sm">
              Each dot represents a PM guest
            </span>
          </motion.div>

          {/* Content - Scrollable container */}
          <motion.div
            className="absolute inset-0 flex flex-col items-center z-10 overflow-y-auto py-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ 
              opacity: isRevealing ? 0 : 1, 
              y: isRevealing ? -12 : 0 
            }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div className="flex flex-col items-center gap-4 px-6 max-w-xl w-full my-auto">
              {/* Logo */}
              <img
                src={fireplaceLogo}
                alt="Latent Lenny"
                className="w-16 h-16"
              />

              {/* Title */}
              <h1 className="title-script text-5xl text-text-primary text-center">
                Latent Lenny
              </h1>

              {/* Subtitle */}
              <p className="text-sm text-text-secondary text-center tracking-widest uppercase">
                A Personality Map for Product Managers
              </p>

              {/* Attribution - between subtitle and toggle */}
              <p className="text-sm text-text-secondary font-medium">
                A Passion Project by{' '}
                <a 
                  href="https://www.linkedin.com/in/benserota/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-text-primary hover:text-orange-primary transition-colors"
                >
                  Ben Serota
                </a>
              </p>

              {/* Unified info container - semi-transparent to let dots peek through */}
              <div className="mt-4 bg-white/85 backdrop-blur-sm border border-warm-border/60 rounded-xl shadow-lg max-w-lg w-full">
                {/* Hero paragraph */}
                <div className="px-6 py-5">
                  <p className="text-base text-text-primary leading-7 text-center">
                    Every guest on Lenny's Podcast has a unique approach to building products. We analyzed <span className="text-orange-primary font-semibold">297 conversations</span> to uncover hidden patterns ("latent variables") of how Lenny's guests think, and created a map - so you can explore, compare, and find where you fit.<br></br>
                    <img
                      src="/images/magic-henning.gif"
                      alt="✨"
                      className="inline-block w-8 h-8 ml-1 -mt-1 align-middle"
                    />
                  </p>
                </div>

                {/* CTA Button */}
                <div className="px-5 pb-5 flex justify-center">
                  <button
                    onClick={handleExplore}
                    className="group px-10 py-3 bg-text-primary text-warm-bg rounded-xl text-base font-semibold transition-all duration-300 ease-out hover:scale-105"
                    style={{
                      boxShadow: '0 2px 8px rgba(255, 107, 53, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#E85A2C';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4), 0 8px 24px rgba(0, 0, 0, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    Explore the Map
                  </button>
                </div>

              </div>

              {/* Footer - Social links only */}
              <div className="mt-3 flex items-center gap-4">
                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/in/benserota/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-warm-border/30 hover:bg-warm-border/60 transition-colors"
                  title="LinkedIn"
                >
                  <svg className="w-5 h-5 text-text-secondary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                {/* GitHub */}
                <a
                  href="https://github.com/BenSerota/lennyhub-rag-main"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-warm-border/30 hover:bg-warm-border/60 transition-colors"
                  title="GitHub"
                >
                  <svg className="w-5 h-5 text-text-secondary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
